import {
  Ban,
  CircleCheck,
  CircleDashed,
  CircleX,
  Download,
  LoaderCircle,
  SkipForward,
  X,
} from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type {
  BlueprintDocument,
  ChangeAction,
  ChildChange,
  Deployment,
  DeploymentResource,
  EventLevel,
  ResourceRunStatus,
  Run,
  RunEvent,
  RunSettingDriftEntry,
} from '../api/types';
import { isTerminalRunStatus } from '../api/types';
import { secretDriftPresentation } from '../components/presentation';
import { StatusBadge } from '../components/ui';
import type { EventLogState } from '../lib/eventLog';
import { formatClock, formatDuration, formatTime } from '../lib/format';
import { childrenSummary, pulumiTypeLabel } from '../lib/planReview';
import { strings } from '../strings';
import { DriftView } from './PlanView';
import { RunIssues } from './RunIssues';

const r = strings.runPage;
const LEVELS: readonly EventLevel[] = ['debug', 'info', 'warning', 'error'];
const RANK: Record<EventLevel, number> = { debug: 0, info: 1, warning: 2, error: 3 };
const LEVEL_CLASS: Record<EventLevel, string> = {
  debug: 'text-fg-muted',
  info: 'text-accent-fg',
  warning: 'text-warning',
  error: 'text-danger',
};

interface Entry {
  id: string;
  label: string;
  action: ChangeAction | null;
  children: ChildChange[];
  /** Listed from the plan, without live status (shared changes, trigger connections). */
  fromPlan: boolean;
}

const STATUS_ICON: Record<ResourceRunStatus, typeof CircleCheck> = {
  pending: CircleDashed,
  'in-progress': LoaderCircle,
  completed: CircleCheck,
  failed: CircleX,
  skipped: SkipForward,
};

function timeZoneName(): string {
  try {
    const part = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    return part?.value ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'local';
  }
}

/** A resource's duration from its status events: first `in-progress` to its last status (running: to now). */
function durations(events: readonly RunEvent[], now: number): Map<string, number> {
  const start = new Map<string, number>();
  const end = new Map<string, number>();
  for (const event of events) {
    if (event.kind !== 'resource-status' || !event.resourceId) continue;
    const at = Date.parse(event.timestamp);
    if (!start.has(event.resourceId)) start.set(event.resourceId, at);
    const status = event.data?.status;
    if (status !== 'in-progress' && status !== 'pending') end.set(event.resourceId, at);
    else end.delete(event.resourceId);
  }
  return new Map([...start].map(([id, from]) => [id, Math.max(0, (end.get(id) ?? now) - from)]));
}

function SettingsDriftList({
  deploymentId,
  drift,
  unavailable,
}: {
  deploymentId: string;
  drift: readonly RunSettingDriftEntry[];
  unavailable: readonly string[];
}) {
  const s = strings.runs;
  return (
    <>
      <h3 className="mt-3 mb-1 text-sm font-semibold">{s.settingsDrift}</h3>
      {drift.length === 0 ? (
        <p className="help">{s.noSettingsDrift}</p>
      ) : (
        <ul className="space-y-1" data-testid="settings-drift">
          {drift.map((entry) => (
            <li
              key={`${entry.resourceId}/${entry.name}`}
              className="flex flex-wrap items-center gap-2 text-sm"
            >
              <span className="font-mono">{entry.resourceId}</span>
              <span className="font-mono font-semibold">{entry.name}</span>
              <span className="badge tone-warning">{s.settingsDriftChange[entry.change]}</span>
              {entry.value !== null && <span className="font-mono text-fg-muted">{entry.value}</span>}
            </li>
          ))}
        </ul>
      )}
      {unavailable.length > 0 && <p className="help">{s.settingsNotChecked(unavailable.join(', '))}</p>}
      {drift.length > 0 && (
        <Link className="link mt-1 inline-block text-sm" to={`/deployments/${deploymentId}`}>
          {s.openVariables}
        </Link>
      )}
    </>
  );
}

/** The live run (spec 6.5, mock m09): progress, terminal callouts, resources and log side by side. */
export function LiveRun({
  run,
  status,
  log,
  deployment,
  boundPlan,
  versionDoc,
  deployedResources,
  onPlanAgain,
  labelOf = (id: string) => id,
  successAction,
  afterSuccess,
  network,
}: {
  run: Run;
  status: Run['status'];
  log: EventLogState;
  deployment: Deployment | undefined;
  /** The plan an apply is bound to (`expectedPlanRunId`). */
  boundPlan: Run | undefined;
  /** The run's blueprint version (trigger connections from the plan). */
  versionDoc: BlueprintDocument | undefined;
  /** The deployment's resources (a destroy lists them). */
  deployedResources: readonly DeploymentResource[] | undefined;
  onPlanAgain: () => void;
  /** Feature spec 17: the label of an entry (a network run's components: "orders-prod · azure / westeurope"). */
  labelOf?: (id: string) => string;
  /** Replaces "Open deployment" in the success callout (a network run: "Open network"). */
  successAction?: ReactNode;
  /** Shown after the success callout (the after-apply callout of an attached deployment, 6.15.15). */
  afterSuccess?: ReactNode;
  /** The run's network, for the networking errors' sentences and actions (6.15.18). */
  network?: { id: string; name: string } | null;
}) {
  const terminal = isTerminalRunStatus(status);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (terminal) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [terminal]);
  const progress = (!terminal && log.progress) || run.progress;
  const [selected, setSelected] = useState<string | null>(null);

  // ---- resources pane, in plan order
  const plan = boundPlan?.result?.plan ?? run.result?.plan ?? null;
  const eventIds = useMemo(() => {
    const ids: string[] = [];
    for (const event of log.events)
      if (event.resourceId && event.kind === 'resource-status' && !ids.includes(event.resourceId))
        ids.push(event.resourceId);
    return ids;
  }, [log.events]);
  const entries: Entry[] = [];
  if (run.kind === 'destroy') {
    for (const id of [...(deployedResources ?? []).map((x) => x.resourceId), ...eventIds])
      if (!entries.some((e) => e.id === id))
        entries.push({ id, label: id, action: 'delete', children: [], fromPlan: false });
  } else if (plan && run.scope !== 'vault') {
    for (const change of plan.changes)
      entries.push({
        id: change.resourceId,
        label: labelOf(change.resourceId),
        action: change.action,
        children: change.children ?? [],
        fromPlan: false,
      });
  }
  for (const id of eventIds)
    if (!entries.some((e) => e.id === id))
      entries.push({ id, label: labelOf(id), action: null, children: [], fromPlan: false });
  if (plan && run.kind !== 'destroy') {
    for (const change of plan.sharedChanges.filter((c) => c.action !== 'no-change'))
      entries.push({
        id: `shared:${change.pulumiType}:${change.name}`,
        label: `${pulumiTypeLabel(change.pulumiType)} ${change.name}`,
        action: change.action,
        children: [],
        fromPlan: true,
      });
    if (run.scope !== 'vault')
      for (const connection of (versionDoc?.connections ?? []).filter((c) => c.type === 'triggers'))
        entries.push({
          id: `trigger:${connection.sourceId}->${connection.targetId}`,
          label: `${connection.sourceId} → ${connection.targetId}`,
          action: null,
          children: [],
          fromPlan: true,
        });
  }
  const times = durations(log.events, now);
  const counts = plan
    ? (['create', 'update', 'replace', 'delete'] as const)
        .map((a) => ({ a, n: plan.changes.filter((c) => c.action === a).length }))
        .filter((x) => x.n > 0)
        .map(({ a, n }) => `${{ create: '+', update: '~', replace: '±', delete: '−' }[a]}${n}`)
        .join(' ')
    : '';

  // ---- log pane
  const [level, setLevel] = useState<EventLevel>('info');
  const [search, setSearch] = useState('');
  const [follow, setFollow] = useState(true);
  const container = useRef<HTMLDivElement>(null);
  const searchId = useId();
  const text = search.trim().toLowerCase();
  const visible = log.events.filter(
    (e) =>
      RANK[e.level] >= RANK[level] &&
      (!selected || e.resourceId === selected) &&
      (!text || `${e.resourceId ?? ''} ${e.message}`.toLowerCase().includes(text)),
  );
  useEffect(() => {
    const element = container.current;
    if (follow && element) element.scrollTop = element.scrollHeight;
  }, [visible.length, follow]);
  const filters = [
    r.filterLevel(r.levelNames[level]),
    ...(selected ? [r.filterResource(selected)] : []),
    ...(text ? [r.filterSearch(search.trim())] : []),
  ].join(', ');
  const download = () => {
    const lines = log.events.map(
      (e) => `${e.timestamp}\t${e.level}\t${e.resourceId ?? '-'}\t${e.message.replace(/\n/g, ' ')}`,
    );
    const url = URL.createObjectURL(new Blob([lines.join('\n') + '\n'], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `run-${run.id}.log`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const kindName = strings.status.runKind[run.kind];
  const logErrors = log.events.filter((e) => e.level === 'error').map((e) => e.message);

  return (
    <div className="live-run">
      {progress && progress.total > 0 && (
        <div className="live-progress">
          <div
            role="progressbar"
            aria-label={r.progress}
            aria-valuemin={0}
            aria-valuemax={progress.total}
            aria-valuenow={progress.completed}
            className="live-progress-bar"
          >
            <div style={{ width: `${Math.round((progress.completed / progress.total) * 100)}%` }} />
          </div>
          <span className="text-sm font-semibold whitespace-nowrap">
            {r.progressText(progress.completed, progress.total)}
          </span>
        </div>
      )}

      <div className="live-callouts">
        {status === 'completed' && (
          <div role="status" className="callout callout-success" data-testid="run-success">
            <CircleCheck aria-hidden className="size-4 shrink-0" />
            <span className="min-w-0 flex-1">
              {run.scope === 'vault' ? r.vaultDone : r.completed(kindName)}
            </span>
            {successAction ? (
              successAction
            ) : run.scope === 'vault' ? (
              <Link className="btn btn-sm" to={`/deployments/${run.deploymentId}?tab=secrets`}>
                {r.setSecretValues}
              </Link>
            ) : (
              <Link className="btn btn-sm" to={`/deployments/${run.deploymentId}`}>
                {r.openDeployment}
              </Link>
            )}
          </div>
        )}
        {status === 'canceled' && (
          <p role="status" className="callout callout-warning" data-testid="run-canceled">
            <Ban aria-hidden className="size-4 shrink-0" />
            {r.canceled}
          </p>
        )}
        {status === 'completed' && afterSuccess}
        <RunIssues
          run={{ ...run, status }}
          deployment={deployment}
          onPlanAgain={onPlanAgain}
          logErrors={logErrors}
          network={network}
        />
        {run.result?.stateMayBeStale && (
          <p className="callout callout-warning">{strings.runs.stateMayBeStale}</p>
        )}
      </div>

      {run.kind === 'refresh' && run.result?.drift && (
        <section className="mb-4" aria-label={strings.runs.drift}>
          <h2 className="mb-2 text-[15px] font-semibold">{strings.runs.drift}</h2>
          <DriftView drift={run.result.drift} />
          {run.result.secretDrift && (
            <>
              <h3 className="mt-3 mb-1 text-sm font-semibold">{strings.runs.secretDrift}</h3>
              {run.result.secretDrift.length === 0 ? (
                <p className="help">{strings.runs.noSecretDrift}</p>
              ) : (
                <ul className="space-y-1" data-testid="secret-drift">
                  {run.result.secretDrift.map((entry) => (
                    <li key={entry.name} className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-mono">{entry.name}</span>
                      <span className="badge tone-neutral">{strings.secrets.kinds[entry.kind]}</span>
                      {entry.drift.map((kind) => (
                        <StatusBadge key={kind} presentation={secretDriftPresentation[kind]} />
                      ))}
                    </li>
                  ))}
                </ul>
              )}
              {run.result.secretDrift.length > 0 && (
                <Link
                  className="link mt-1 inline-block text-sm"
                  to={`/deployments/${run.deploymentId}?tab=secrets`}
                >
                  {strings.runs.openSecrets}
                </Link>
              )}
            </>
          )}
          {run.result.settingsChecked && (
            <SettingsDriftList
              deploymentId={run.deploymentId}
              drift={run.result.settingsDrift ?? []}
              unavailable={(run.result.diagnostics ?? [])
                .filter((d) => d.code === 'SETTINGS_DRIFT_UNAVAILABLE' && d.resourceId)
                .map((d) => d.resourceId!)}
            />
          )}
        </section>
      )}

      <div className="live-panes">
        <section className="live-resources" aria-labelledby="live-resources-heading">
          <details open className="live-resources-details">
            <summary className="live-pane-head">
              <h2 id="live-resources-heading" className="text-sm font-semibold">
                {r.resources}
              </h2>
              <span className="text-xs text-fg-muted">
                {boundPlan
                  ? `${r.actionsFromPlan(formatClock(boundPlan.finishedAt))}${counts ? `: ${counts}` : ''}`
                  : !run.result?.plan && run.kind === 'apply' && run.scope !== 'vault'
                    ? r.actionsAfterCompletion
                    : counts}
              </span>
            </summary>
            {entries.length === 0 ? (
              <p className="help p-3">{r.noResources}</p>
            ) : (
              <ul className="live-entries">
                {entries.map((entry) => {
                  const value = entry.fromPlan ? undefined : log.resourceStatus[entry.id];
                  const statusValue: ResourceRunStatus | null = entry.fromPlan
                    ? null
                    : (value ?? (entry.action === 'no-change' ? null : 'pending'));
                  const Icon = statusValue ? STATUS_ICON[statusValue] : CircleDashed;
                  const statusWord = entry.fromPlan
                    ? r.fromPlan
                    : statusValue
                      ? r.statusWords[statusValue]
                      : r.noChange;
                  const detail = [
                    entry.action && entry.action !== 'no-change'
                      ? strings.status.action[entry.action].toLowerCase()
                      : null,
                    ...childrenSummary(entry.children),
                    entry.id.startsWith('trigger:') ? r.trigger : null,
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  const time = times.get(entry.id);
                  return (
                    <li key={entry.id}>
                      <button
                        type="button"
                        className="live-entry"
                        aria-pressed={selected === entry.id}
                        disabled={entry.fromPlan}
                        data-testid={entry.fromPlan ? undefined : `resource-${entry.id}`}
                        onClick={() => setSelected(selected === entry.id ? null : entry.id)}
                      >
                        <Icon
                          aria-hidden
                          className={`size-4 shrink-0 ${statusValue === 'completed' ? 'text-success' : statusValue === 'failed' ? 'text-danger' : statusValue === 'in-progress' ? 'text-accent-fg' : 'text-fg-muted'}`}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="font-mono font-semibold">{entry.label}</span>{' '}
                          {detail && <span className="text-xs text-fg-muted">{detail}</span>}
                          <span className="block text-xs text-fg-muted">{statusWord}</span>
                        </span>
                        <span className="text-xs text-fg-muted tabular-nums">
                          {time !== undefined ? formatDuration(time) : strings.common.dash}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <p className="help px-3 py-2">{r.selectHint}</p>
          </details>
        </section>

        <section className="live-log" aria-labelledby="live-log-heading">
          <div className="live-pane-head flex-wrap">
            <h2 id="live-log-heading" className="text-sm font-semibold">
              {r.log}
            </h2>
            <div className="segmented" role="group" aria-label={r.levels}>
              {LEVELS.map((l) => (
                <button
                  key={l}
                  type="button"
                  className="segmented-item"
                  aria-pressed={level === l}
                  onClick={() => setLevel(l)}
                >
                  {r.levelNames[l]}
                </button>
              ))}
            </div>
            {selected && (
              <span className="chip">
                <span className="font-mono">{selected}</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-icon"
                  aria-label={r.removeResource(selected)}
                  onClick={() => setSelected(null)}
                >
                  <X aria-hidden className="size-3.5" />
                </button>
              </span>
            )}
            <label className="sr-only" htmlFor={searchId}>
              {r.searchLog}
            </label>
            <input
              id={searchId}
              type="search"
              className="input w-48"
              placeholder={r.searchLog}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="flex-1" />
            <button
              type="button"
              className="btn btn-sm"
              aria-pressed={follow}
              onClick={() => setFollow((value) => !value)}
            >
              {r.follow}
            </button>
            <button type="button" className="btn btn-sm" onClick={download}>
              <Download aria-hidden className="size-3.5" />
              {r.download}
            </button>
          </div>
          <div
            ref={container}
            tabIndex={0}
            role="log"
            aria-label={r.log}
            className="live-log-body"
            onKeyDown={(event) => {
              if (event.key === 'End') setFollow(true);
            }}
            onScroll={(event) => {
              const element = event.currentTarget;
              const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 8;
              if (!atBottom && follow) setFollow(false);
            }}
          >
            {visible.length === 0 ? (
              <p className="p-2 font-sans text-fg-muted">{r.noEvents}</p>
            ) : (
              <ol>
                {visible.map((event) => {
                  return (
                    <li key={event.sequence} className="live-log-line">
                      <time dateTime={event.timestamp} className="text-fg-muted">
                        {formatTime(event.timestamp)}
                      </time>
                      <span className={LEVEL_CLASS[event.level]}>{event.level}</span>
                      <span className="truncate text-fg-muted">{event.resourceId ?? 'run'}</span>
                      <span className="min-w-0 break-words">{event.message}</span>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
          <p className="live-log-footer">
            {r.footer(visible.length, log.events.length, filters, timeZoneName())}
          </p>
        </section>
      </div>
    </div>
  );
}
